import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api, type Broker, type Holding, type ImportResult } from "../lib/api.js";
import { AuthGate } from "../components/AuthGate.js";
import { ErrorText, Field } from "../components/ui.js";

export function HoldingsPage() {
  return (
    <AuthGate>
      <HoldingsInner />
    </AuthGate>
  );
}

function HoldingsInner() {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [error, setError] = useState<unknown>(null);

  const reload = () => api.holdings().then((r) => setHoldings(r.holdings)).catch(setError);
  useEffect(() => {
    void reload();
    api.brokers().then((r) => setBrokers(r.brokers)).catch(setError);
  }, []);

  const brokerName = (id: string | null) => brokers.find((b) => b.id === id)?.name ?? "未設定";

  const grouped = useMemo(() => {
    const m = new Map<string, Holding[]>();
    for (const h of holdings) m.set(h.stockCode, [...(m.get(h.stockCode) ?? []), h]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [holdings]);

  return (
    <section>
      <h1>所持銘柄</h1>
      <ErrorText error={error} />
      {brokers.length === 0 && (
        <p className="muted">
          先に <Link to="/my/brokers">証券会社設定</Link> を追加すると、ロットに証券会社を紐づけられます。
        </p>
      )}
      <AddLotForm brokers={brokers} onAdded={reload} />
      <CsvImport brokers={brokers} onImported={reload} />
      {grouped.map(([code, lots]) => (
        <div className="card" key={code}>
          <div className="row between">
            <h2>
              <Link to={`/stocks/${code}`}>{code}</Link> {lots[0]?.stockName ?? ""}
            </h2>
            <span>{lots.reduce((s, l) => s + l.shares, 0).toLocaleString("ja-JP")}株</span>
          </div>
          <ul className="list">
            {lots.map((l) => (
              <li key={l.id} className="row between">
                <span>
                  {brokerName(l.brokerSettingId)} / {l.shares.toLocaleString("ja-JP")}株 / 取得日 {l.acquiredOn ?? <em className="warn">未設定</em>}
                  {l.unitPrice != null && ` / ${l.unitPrice.toLocaleString("ja-JP")}円`}
                </span>
                <LotActions lot={l} onChanged={reload} />
              </li>
            ))}
          </ul>
        </div>
      ))}
      {holdings.length === 0 && <p className="muted">まだ所持銘柄がありません。</p>}
    </section>
  );
}

function LotActions({ lot, onChanged }: { lot: Holding; onChanged: () => Promise<unknown> }) {
  const setDate = async () => {
    const v = prompt("取得日 (YYYY-MM-DD)", lot.acquiredOn ?? "");
    if (v === null) return;
    await api.updateHolding(lot.id, { acquiredOn: v || null });
    await onChanged();
  };
  const remove = async () => {
    if (!confirm("このロットを削除しますか？")) return;
    await api.deleteHolding(lot.id);
    await onChanged();
  };
  return (
    <span>
      <button className="btn small" type="button" onClick={setDate}>
        取得日
      </button>
      <button className="btn small danger" type="button" onClick={remove}>
        削除
      </button>
    </span>
  );
}

function AddLotForm({ brokers, onAdded }: { brokers: Broker[]; onAdded: () => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [brokerId, setBrokerId] = useState("");
  const [shares, setShares] = useState("100");
  const [acquiredOn, setAcquiredOn] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.createHolding({
        stockCode: code.toUpperCase(),
        stockName: name || null,
        brokerSettingId: brokerId || null,
        shares: Number(shares),
        acquiredOn: acquiredOn || null,
        unitPrice: unitPrice ? Number(unitPrice) : null,
        note: null,
      });
      setCode("");
      setName("");
      setOpen(false);
      await onAdded();
    } catch (err) {
      setError(err);
    }
  };

  if (!open) {
    return (
      <button className="btn primary" type="button" onClick={() => setOpen(true)}>
        手動で追加
      </button>
    );
  }
  return (
    <form className="card form" onSubmit={submit}>
      <Field label="証券コード">
        <input className="input" required pattern="[0-9][0-9A-Za-z]{3}" value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <Field label="会社名（任意）">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="証券会社">
        <select className="input" value={brokerId} onChange={(e) => setBrokerId(e.target.value)}>
          <option value="">未設定</option>
          {brokers.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="株数">
        <input className="input" type="number" min={1} required value={shares} onChange={(e) => setShares(e.target.value)} />
      </Field>
      <Field label="取得日（任意）">
        <input className="input" type="date" value={acquiredOn} onChange={(e) => setAcquiredOn(e.target.value)} />
      </Field>
      <Field label="取得単価（任意）">
        <input className="input" type="number" min={0} step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} />
      </Field>
      <ErrorText error={error} />
      <div className="row">
        <button className="btn primary" type="submit">
          追加
        </button>
        <button className="btn" type="button" onClick={() => setOpen(false)}>
          キャンセル
        </button>
      </div>
    </form>
  );
}

function CsvImport({ brokers, onImported }: { brokers: Broker[]; onImported: () => Promise<unknown> }) {
  const [brokerId, setBrokerId] = useState("");
  const [preview, setPreview] = useState<{ kind: string; broker: string; balances: number; trades: number; warnings: string[] } | null>(null);
  const [parsed, setParsed] = useState<Awaited<ReturnType<typeof parseFile>> | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const p = await parseFile(file);
      setParsed(p);
      setPreview({ kind: p.kind, broker: p.broker, balances: p.balances.length, trades: p.trades.length, warnings: p.warnings });
    } catch (e) {
      setError(e);
      setParsed(null);
      setPreview(null);
    }
  };

  const confirmImport = async () => {
    if (!parsed || !brokerId) return;
    setError(null);
    try {
      const r = await api.importHoldings({
        brokerSettingId: brokerId,
        balances: parsed.balances.map((b) => ({ code: b.code, name: b.name, shares: b.shares, ...(b.unitPrice !== undefined ? { unitPrice: b.unitPrice } : {}) })),
        trades: parsed.trades.map((t) => ({ code: t.code, name: t.name, side: t.side, shares: t.shares, price: t.price, tradeDate: t.tradeDate })),
      });
      setResult(r);
      setParsed(null);
      setPreview(null);
      await onImported();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <div className="card">
      <h2>CSV 取り込み</h2>
      <p className="muted">保有証券 CSV または約定履歴 CSV（SBI・楽天・マネックス）。文字コードは自動判定します。</p>
      <div className="form">
        <Field label="取り込み先の証券会社">
          <select className="input" value={brokerId} onChange={(e) => setBrokerId(e.target.value)}>
            <option value="">選択してください</option>
            {brokers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <input className="input" type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} />
      </div>
      <ErrorText error={error} />
      {preview && (
        <div>
          <p>
            判定: {preview.broker} / {preview.kind === "balance" ? "保有証券" : "約定履歴"} / 保有 {preview.balances} 件 / 約定 {preview.trades} 件
          </p>
          {preview.warnings.length > 0 && (
            <ul className="list muted">
              {preview.warnings.slice(0, 10).map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
          <button className="btn primary" type="button" disabled={!brokerId} onClick={confirmImport}>
            取り込む
          </button>
        </div>
      )}
      {result && (
        <div>
          <p>追加 {result.added.length} 件、変更なし {result.unchanged.length} 件</p>
          {result.needsReview.length > 0 && (
            <p className="warn">
              株数が一致しない銘柄: {result.needsReview.map((r) => `${r.code}（CSV ${r.csvShares} / 登録 ${r.lotShares}）`).join("、")}
            </p>
          )}
          {result.sells.length > 0 && <p className="warn">売却の約定 {result.sells.length} 件は自動反映していません。該当ロットを手動で調整してください。</p>}
        </div>
      )}
    </div>
  );
}

async function parseFile(file: File) {
  const { decodeCsvBytes, parseBrokerCsv } = await import("@yct/core/csv");
  const bytes = new Uint8Array(await file.arrayBuffer());
  return parseBrokerCsv(decodeCsvBytes(bytes));
}
